import React, { useState, useEffect } from 'react';
import { Activity, Users, RefreshCw, AlertCircle, BarChart3, User, CalendarDays, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend
} from 'recharts';
import { accessLogService } from '../../services';
import Card from '../ui/Card';
import LoadMoreButton from '../ui/LoadMoreButton';

const COLORS = ['#3182CE', '#4FD1C5', '#F6AD55', '#FC8181', '#9F7AEA', '#667EEA', '#ED64A1'];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
        <p className="text-gray-600 text-sm mb-1 font-medium">{label || payload[0].name}</p>
        <p className="text-blue-600 font-bold">
          {payload[0].value.toLocaleString()} <span className="text-xs text-gray-500 font-normal">次</span>
        </p>
      </div>
    );
  }
  return null;
};

const AccessReportView = ({ searchTrigger }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    // Wait for tab switch animation and layout to fully settle
    const timer = setTimeout(() => setMounted(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const [data, setData] = useState({
    total_pv: 0,
    total_uv: 0,
    hourly_heat: [],
    user_pv_ranking: [],
    module_distribution: [],
    system_distribution: []
  });

  // ... (logs states remain same)
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsData, setLogsData] = useState({ items: [], total: 0 });
  const [logsPageSize, setLogsPageSize] = useState(20);
  const [loadingMore, setLoadingMore] = useState(false);
  const [logsSearch, setLogsSearch] = useState('');
  const [tempSearchInput, setTempSearchInput] = useState('');

  const loadData = async () => {
    console.log('AccessReportView: Starting to load data with days:', days);
    try {
      setLoading(true);
      setError(null);
      
      const res = await accessLogService.getDailyReport(days);
      
      if (res && res.data) {
        setData(res.data);
      } else {
        setData({
          total_pv: 0,
          total_uv: 0,
          hourly_heat: [],
          user_pv_ranking: [],
          module_distribution: [],
          system_distribution: []
        });
      }
    } catch (err) {
      console.error('获取系统访问统计失败:', err);
      setError(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async (isLoadMore = false) => {
    try {
      if (isLoadMore) setLoadingMore(true);
      else setLogsLoading(true);

      const nextLimit = isLoadMore ? logsPageSize + 20 : 20;
      if (!isLoadMore) setLogsPageSize(20);
      else setLogsPageSize(nextLimit);

      const res = await accessLogService.getAccessLogsList({
        page: 1, // Always page 1 because we use limit for Load More
        page_size: nextLimit,
        search: logsSearch
      });
      if (res && res.data) {
        setLogsData(res.data);
      }
    } catch (err) {
      console.error('获取访问明细失败:', err);
    } finally {
      setLogsLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!logsLoading && !loadingMore && logsData.items.length < logsData.total) {
      loadLogs(true);
    }
  };

  useEffect(() => {
    loadData();
  }, [searchTrigger, days]);

  useEffect(() => {
    loadLogs();
  }, [logsSearch, searchTrigger]);

  const handleSearch = () => {
    setLogsSearch(tempSearchInput);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const totalPages = Math.ceil(logsData.total / logsPageSize);

  return (
    <div className="space-y-3 font-sans">
      {/* 顶部控制栏 */}
      <Card className="h-[48px] px-3 flex justify-end items-center gap-2 min-w-0 bg-white/80 backdrop-blur-sm border-[#EDF2F7]">
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {[3, 7, 30].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-4 py-1 text-[11px] font-black rounded-md transition-all ${
                  days === d 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {d}天
              </button>
            ))}
          </div>
        </div>
      </Card>

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-700 font-medium">加载失败</p>
            <p className="text-xs text-red-600 mt-1">{error}</p>
            <button onClick={loadData} className="mt-2 text-xs text-red-700 underline">
              重试
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          
          {/* 左侧 KPI & 排行榜 */}
          <div className="lg:col-span-1 space-y-3 flex flex-col min-w-0">
            <Card className="p-3">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 size={16} className="text-[#3182CE]" />
                <h3 className="font-bold text-base text-[#2D3748]">核心指标 ({days}天)</h3>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-blue-50/50 p-2 rounded-lg border border-blue-100 flex flex-col justify-center">
                  <div className="text-[11px] text-blue-600 font-bold flex items-center gap-1 mb-0.5 uppercase">
                    <Activity size={10} /> PV
                  </div>
                  <div className="text-xl font-black text-blue-700 leading-none">
                    {loading ? '...' : data.total_pv.toLocaleString()}
                  </div>
                </div>
                <div className="bg-indigo-50/50 p-2 rounded-lg border border-indigo-100 flex flex-col justify-center">
                  <div className="text-[11px] text-indigo-600 font-bold flex items-center gap-1 mb-0.5 uppercase">
                    <Users size={10} /> UV
                  </div>
                  <div className="text-xl font-black text-indigo-700 leading-none">
                    {loading ? '...' : data.total_uv.toLocaleString()}
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-3 flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <User size={16} className="text-[#3182CE]" />
                <h3 className="font-bold text-base text-[#2D3748]">用户 PV 排行 Top 10</h3>
              </div>
              <div className="space-y-1.5 flex-1 overflow-y-auto pr-1">
                {loading ? (
                  <div className="py-8 text-center text-gray-400 text-[10px]">加载中...</div>
                ) : data.user_pv_ranking.length === 0 ? (
                  <div className="py-8 text-center text-gray-400 text-[10px]">暂无数据</div>
                ) : (
                  data.user_pv_ranking.map((user, idx) => (
                    <div key={user.rank} className={`flex justify-between items-center p-2 rounded-md text-xs ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} border border-gray-50`}>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className={`w-4 h-4 flex items-center justify-center rounded-full text-[9px] font-black ${
                          user.rank === 1 ? 'bg-yellow-100 text-yellow-600' :
                          user.rank === 2 ? 'bg-gray-200 text-gray-600' :
                          user.rank === 3 ? 'bg-orange-100 text-orange-600' :
                          'text-gray-400 bg-gray-100'
                        }`}>
                          {user.rank}
                        </span>
                        <span className="font-bold text-[#4A5568] truncate">{user.name}</span>
                      </div>
                      <div className="font-black text-blue-600 ml-2">{user.pv}</div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>

          {/* 右侧分布图 & 热度图 */}
          <div className="lg:col-span-2 space-y-4 min-w-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="p-3 h-[200px]">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 size={14} className="text-[#3182CE]" />
                  <h3 className="font-bold text-[#2D3748] text-xs uppercase tracking-wider">模块分布</h3>
                </div>
                <div className="w-full h-[150px]">
                  {(loading || !mounted) ? <div className="h-full flex items-center justify-center text-gray-400 text-[10px]">加载中...</div> : (
                    <ResponsiveContainer width="100%" height={150} minWidth={0} minHeight={0}>
                      <PieChart>
                        <Pie
                          data={data.module_distribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={30}
                          outerRadius={55}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {data.module_distribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend iconSize={6} wrapperStyle={{ fontSize: '11px' }} verticalAlign="bottom" height={36}/>
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </Card>

              <Card className="p-3 h-[200px]">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 size={14} className="text-[#3182CE]" />
                  <h3 className="font-bold text-[#2D3748] text-xs uppercase tracking-wider">来源分布</h3>
                </div>
                <div className="w-full h-[150px]">
                  {(loading || !mounted) ? <div className="h-full flex items-center justify-center text-gray-400 text-[10px]">加载中...</div> : (
                    <ResponsiveContainer width="100%" height={150} minWidth={0} minHeight={0}>
                      <PieChart>
                        <Pie
                          data={data.system_distribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={30}
                          outerRadius={55}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {data.system_distribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[(index + 3) % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend iconSize={6} wrapperStyle={{ fontSize: '11px' }} verticalAlign="bottom" height={36}/>
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </Card>
            </div>

            <Card className="p-3">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <CalendarDays size={16} className="text-[#3182CE]" />
                  <h3 className="font-bold text-base text-[#2D3748]">24小时时段热度</h3>
                </div>
              </div>
              
              <div className="w-full" style={{ height: '160px' }}>
                {!loading && mounted && (
                  <ResponsiveContainer width="100%" height={160} minWidth={0} minHeight={0}>
                    <BarChart data={data.hourly_heat} margin={{ top: 10, right: 10, left: -30, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EDF2F7" />
                      <XAxis dataKey="hour" stroke="#8892B0" fontSize={10} tickLine={false} axisLine={false} />
                      <YAxis stroke="#8892B0" fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F7FAFC' }} />
                      <Bar dataKey="value" radius={[2, 2, 0, 0]} maxBarSize={15}>
                        {data.hourly_heat.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.value > 0 ? "#3182CE" : "#E2E8F0"} 
                            fillOpacity={entry.value > 0 ? 0.7 : 1}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* 访问日志明细 */}
      <Card className="p-3 flex flex-col">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-3">
          <div className="flex items-center gap-2">
            <User size={16} className="text-[#3182CE]" />
            <h3 className="font-bold text-base text-[#2D3748]">访问明细</h3>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <Search size={14} />
              </span>
              <input
                type="text"
                placeholder="搜索内容..."
                value={tempSearchInput}
                onChange={(e) => setTempSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 w-40 sm:w-56"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs font-bold hover:bg-blue-100 transition-colors"
            >
              搜索
            </button>
            <button
              onClick={() => loadLogs()}
              className="p-1.5 text-gray-500 hover:text-blue-600 bg-gray-50 hover:bg-blue-50 rounded-lg transition-colors border border-gray-100"
              title="刷新"
            >
              <RefreshCw size={14} className={logsLoading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[13px] text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-500 font-bold text-xs">
                <th className="px-3 py-2 rounded-tl-lg">时间</th>
                <th className="px-3 py-2">学生</th>
                <th className="px-3 py-2">路径/模块</th>
                <th className="px-3 py-2 rounded-tr-lg">设备</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logsLoading && logsData.items.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-3 py-8 text-center text-gray-400 italic text-[10px]">检索中...</td>
                </tr>
              ) : logsData.items.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-3 py-8 text-center text-gray-400 italic text-[10px]">暂无明细</td>
                </tr>
              ) : (
                logsData.items.map((log, idx) => (
                  <tr key={log.id} className={`hover:bg-blue-50/30 transition-colors border-b border-gray-50 last:border-0 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/20'}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-400">
                      {log.time ? new Date(log.time).toLocaleString('zh-CN', { 
                        month: '2-digit', 
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : '--'}
                    </td>
                    <td className="px-3 py-2">
                        <span className="font-black text-[#2D3748] truncate block max-w-[80px]">
                          {log.student_names || log.username}
                        </span>
                    </td>
                    <td className="px-3 py-2">
                        <span className="text-[#4A5568] font-bold truncate block max-w-[120px]" title={log.path_original}>
                          {log.path_desc || log.path_original}
                        </span>
                    </td>
                    <td className="px-3 py-2 text-gray-400 truncate max-w-[100px]" title={log.user_agent}>
                      {log.device_desc}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 加载分页 */}
        <LoadMoreButton 
          onClick={handleLoadMore} 
          loading={loadingMore} 
          hasMore={logsData.items.length > 0 && logsData.items.length < logsData.total} 
        />
      </Card>
      
    </div>
  );
};

export default AccessReportView;
